import { Identify } from "@amplitude/analytics-browser";
import {
  investorAmplitude,
  isInvestorInitialized
} from "../../utils/amplitude-config";
import { ingestActivity } from "../../utils/activity-ingest";

// Types
interface BaseEventProperties {
  timestamp?: string;
  userId?: string;
  sfid?: string;
}

interface PropertyEventProperties extends BaseEventProperties {
  propertySFID: string;
  propertyId: string;
  shareId: string;
  pubPropertySFID: string;
  opportunitySFID: string;
  linkCId?: string;
  linkEmailId?: string;
  mobileNumber?: string;
  address?: string;
  bedRooms?: number;
  baths?: number;
  sqft?: number;
  askingPrice?: number;
}

interface DocumentEventProperties extends PropertyEventProperties {
  DocumentName: string;
  DocumentUrl: string;
  mobileNumber: string;
}

interface ImageEventProperties extends PropertyEventProperties {
  image_url?: string;
  totalImagesViewed?: number;
}

// Helper function to get user data from localStorage
const getUserData = () => {
  const userId = localStorage.getItem("userId") || "";
  const sfid = localStorage.getItem("sfid") || "";
  return { userId, sfid };
};

// Helper function to track events with error handling
//
// Sends every event twice: once to our own ingest (which is what actually feeds
// investor_activities and the interest score) and once to Amplitude (which we can no longer
// administer, but may still be relaying). See utils/activity-ingest.ts.
//
// Our ingest deliberately runs OUTSIDE the isInvestorInitialized check. That flag is false
// whenever REACT_APP_AMPLITUDE_KEY is unset — so clearing the dead Amplitude key out of
// .env.prod, an obvious bit of cleanup, would otherwise silently take the score feed down
// with it.
const trackEvent = (eventName: string, properties: Record<string, any>) => {
  const mobileNumber = localStorage.getItem("mobileNumber");
  let eventProperties: Record<string, any> = properties;
  try {
    const userData = getUserData();
    eventProperties = {
      ...userData,
      ...properties,
      timestamp: new Date().toISOString(),
      // Only let the stored number fill a gap. Assigning it unconditionally (as this did)
      // overwrote a perfectly good mobileNumber from `properties` with null whenever
      // localStorage had not been populated yet — which is the case for the whole
      // pre-OTP funnel, exactly where the server reads this field.
      ...(mobileNumber ? { mobileNumber } : {})
    };
  } catch (error) {
    console.error(`Failed to build event ${eventName}:`, error);
  }

  ingestActivity(eventName, eventProperties);

  if (!isInvestorInitialized) return;
  try {
    investorAmplitude.track(eventName, eventProperties);
  } catch (error) {
    console.error(`Failed to track event ${eventName}:`, error);
  }
};

// Helper function to check if current page is an investor page
const isInvestorPage = (pathname: string): boolean => {
  const investorPaths = [
    "/investor",
    "/investor-login",
    "/investor-property-details",
    "/opt-verification"
  ];
  return investorPaths.some((path) => pathname.startsWith(path));
};

// Helper function to track page view only for investor pages
export const trackPageView = (reqBody: {
  pathname: string;
  linkCId: string;
  linkEmailId: string;
  mobileNumber?: string;
}) => {
  if (!isInvestorPage(reqBody.pathname)) return;

  try {
    const userData = getUserData();
    const eventProperties = {
      ...userData,
      path: reqBody.pathname,
      linkCId: reqBody.linkCId,
      linkEmailId: reqBody.linkEmailId,
      ...(reqBody.mobileNumber && { mobileNumber: reqBody.mobileNumber }),
      timestamp: new Date().toISOString()
    };

    ingestActivity("Page View", eventProperties);

    if (!isInvestorInitialized) return;
    investorAmplitude.track("Page View", eventProperties);
  } catch (error) {
    console.error("Failed to track page view:", error);
  }
};

// Event tracking functions
export const PropertyViewed = (properties: PropertyEventProperties) => {
  trackEvent("Property Details Viewed", properties);
};

export const DocumentViewed = (properties: DocumentEventProperties) => {
  trackEvent(`${properties.DocumentName} Document Viewed`, properties);
};

export const SharedLinkClicked = (reqBody: {
  sfid?: string;
  linkCId: string;
  linkEmailId: string;
  mobileNumber?: string;
}) => {
  try {
    const userData = getUserData();

    trackEvent("Share link Clicked", {
      ...reqBody,
      sfid: userData.sfid || reqBody.linkCId,
      linkCId: reqBody.linkCId,
      ...(reqBody.mobileNumber && { mobileNumber: reqBody.mobileNumber })
    });
  } catch (error) {
    console.error("Failed to track login event:", error);
  }
};

export const LoginWithOTP = (reqBody: {
  // Optional: send-otp no longer returns it. Amplitude identity falls back to linkCId /
  // mobileNumber below, and the real sfid lands at verify-otp.
  sfid?: string;
  mobileNumber: string;
  linkCId: string;
  linkEmailId: string;
}) => {
  try {
    // App state, not analytics — every later event resolves its contact through it.
    //
    // Guarded now because send-otp NO LONGER RETURNS sfid: handing a Salesforce ID to an
    // unauthenticated caller is what let an attacker turn "insert junk" into "overwrite a
    // real investor record" via /sf-sync/contact. It arrives from verify-otp instead, once
    // the caller has proven they hold the phone. Without this guard we would write the
    // string "undefined" over a previously valid value.
    if (reqBody?.sfid) {
        localStorage.setItem("sfid", reqBody.sfid);
    }

    if (isInvestorInitialized) {
      // One identity value for both calls. setUserId already fell back through linkCId and
      // mobileNumber; identify() did not, and passed sfid straight through — which is now
      // undefined at this point in the flow. Using the same resolved value keeps Amplitude
      // identity stable pre-verification instead of identifying the session as nothing.
      const identityValue =
        reqBody?.sfid || reqBody.linkCId || reqBody.mobileNumber;
      investorAmplitude.setUserId(identityValue);
      const identify = new Identify().set("userId", identityValue);
      investorAmplitude.identify(identify);
    }

    trackEvent("Start Session", {
      sfid: reqBody?.sfid,
      mobileNumber: reqBody?.mobileNumber,
      linkCId: reqBody?.linkCId,
      linkEmailId: reqBody?.linkEmailId
    });
    trackEvent("Login with OTP Button Clicked", {
      ...reqBody,
      sfid: reqBody?.sfid,
      mobileNumber: reqBody?.mobileNumber
    });
  } catch (error) {
    console.error("Failed to track login event:", error);
  }
};

export const ChangeMobileNo = (reqBody: {
  mobileNumber: string;
  linkCId: string;
  linkEmailId: string;
}) => {
  trackEvent("Change Mobile No Clicked", reqBody);
};

export const ResendOTPClicked = (reqBody: {
  mobileNumber: string;
  linkCId: string;
  linkEmailId: string;
}) => {
  trackEvent("Resend OTP Clicked", { ...reqBody, sfid: reqBody.linkCId });
};

export const VerifyButtonClicked = (data: {
  contactId?: string;
  sfid: string;
  mobileNumber: string;
  linkCId: string;
  linkEmailId: string;
}) => {
  try {
    // As with the sfid write above: app state, so it must not depend on Amplitude.
    if (data?.contactId) {
      localStorage.setItem("userId", data?.contactId);
    }
    // sfid now lands HERE rather than at send-otp — this is the first point in the flow
    // where the caller has actually authenticated, so it is the first point at which it is
    // safe for them to learn it.
    if (data?.sfid) {
      localStorage.setItem("sfid", data.sfid);
    }
    trackEvent("Verify Button Clicked", {
      userId: data?.contactId,
      mobileNumber: data.mobileNumber,
      linkCId: data.linkCId,
      linkEmailId: data.linkEmailId
    });
  } catch (error) {
    console.error("Failed to set user ID:", error);
  }
};

export const TotalTimeSpent = (
  properties: PropertyEventProperties & {
    timeSpent: number;
    mobileNumber: string;
  }
) => {
  trackEvent("Total Time Spent", {
    ...properties,
    duration: properties.timeSpent
  });
};

export const ViewMore = (properties: PropertyEventProperties) => {
  trackEvent("View More Clicked", properties);
};

export const PropertyDetailImageClick = (properties: ImageEventProperties) => {
  trackEvent("Property Detail Image Clicked", properties);
};

export const TotalPropertyImageViewed = (properties: ImageEventProperties) => {
  trackEvent("Total Property Image Viewed", properties);
};

export const TotalFullScreenImageViewed = (
  properties: ImageEventProperties
) => {
  trackEvent("Total Full Screen Image Viewed", properties);
};

export const SeeAllFeatures = (
  properties: PropertyEventProperties & { buttonLabel?: string }
) => {
  trackEvent("See All Features Clicked", properties);
};

export const LogoutClicked = (properties: PropertyEventProperties) => {
  trackEvent("Logout Clicked", properties);
  investorAmplitude.reset();
};

export const ContactAgents = (properties: PropertyEventProperties) => {
  trackEvent("Contact Agents Clicked", properties);
};
//Done
export const LikedProperty = (properties: PropertyEventProperties) => {
  trackEvent("Property Like Clicked", properties);
};

//Done
export const unLikedProperty = (properties: PropertyEventProperties) => {
  trackEvent("Property unLike Clicked", properties);
};

//Done
export const NavigatePropertyDetail = (properties: PropertyEventProperties) => {
  trackEvent(`User navigate to Details Page`, properties);
};

//Done
export const SortApplied = (properties: any) => {
  trackEvent("User applies sort", properties);
};

// Done
export const ToggleMenu = (view: string) => {
  trackEvent(`User clicked on ${view}`, {});
};

// Done
export const ToggleView = (layout: string) => {
  trackEvent(`User toggle to ${layout} view`, {});
};

// Done
export const ToggleDashboardMenu = (tabName: string) => {
  trackEvent(`User switches to ${tabName} tab`, {});
};

// Done
export const searchProperties = (searchTerm: string, totalCount: number) => {
  trackEvent(`User runs a search`, {
    searchTerm: searchTerm,
    totalCount: totalCount
  });
};

// Done
export const MapPinClicked = (properties: PropertyEventProperties) => {
  trackEvent(`User clicked on map pin`, properties);
};

// Done
export const MapBtnToggled = (label: string) => {
  trackEvent(`User Toggle to ${label}`, {});
};
